import {
  Card,
  CircleEmptyInfoIcon,
  InfoBanner,
  LeftArrowIcon,
  ThemeProvider,
} from "@codat/orchard-ui";
import { SourceTypeResponse } from "api/company";
import classNames from "classnames";
import { useDemoMode } from "contexts/DemoModeContext";
import { useSourceTypes } from "contexts/SourceTypesContext";
import { PropsWithChildren, useEffect, useState } from "react";

import Button from "components/Button";
import ErrorFallback from "components/ErrorFallback";
import LeftHandPane from "components/LeftHandPane";
import PlaidUpgrade from "components/PlaidUpgrade";
import { PlaidUpgradeProps } from "components/PlaidUpgrade/PlaidUpgrade";
import RightHandPane from "components/RightHandPane";
import SecondaryCta from "components/SecondaryCta";
import Spinner from "components/Spinner";

import styles from "./LinkPageRoot.module.css";

type LinkRightHandPaneProps = Omit<
  React.ComponentProps<typeof RightHandPane>,
  "onInfoClick"
>;
export type LinkPageRootProps = {
  showLinkedAccounts?: boolean;
  currentSourceType?: SourceTypeResponse;
  isPlaidClientLimitReached?: boolean;
  className?: string;
} & LinkRightHandPaneProps &
  PlaidUpgradeProps;

export const LinkPageRoot: React.FC<PropsWithChildren<LinkPageRootProps>> = ({
  showLinkedAccounts,
  title,
  onBackClick,
  onExitClick,
  primaryCtaProps,
  secondaryCtaProps,
  footer,
  isComplete,
  children,
  currentSourceType,
  integrationLogo,
  isPlaidClientLimitReached,
  className,
}) => {
  const [isInfoPanelOpen, setInfoPanelOpen] = useState(false);

  const { error } = useSourceTypes();
  const { isDemo } = useDemoMode();

  const handleBack = () => {
    setInfoPanelOpen(false);
  };

  const handleInfo = () => {
    setInfoPanelOpen(true);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [isInfoPanelOpen]);

  if (error) {
    return <ErrorFallback error={error} />;
  }

  return (
    <div className={classNames(className, styles.container)}>
      {isDemo && (
        <ThemeProvider>
          {() => (
            <div data-testid="demo-mode-indicator-banner">
              <InfoBanner
                className={styles.banner}
                title="You're using demo mode"
                subtitle="All integrations will connect to Codat Sandbox"
                icon={<CircleEmptyInfoIcon />}
                color="action"
              />
            </div>
          )}
        </ThemeProvider>
      )}
      <Card className={styles.card}>
        <section
          data-testid="root-left-section"
          className={classNames(styles.pane, styles.leftPane, {
            [styles.open]: isInfoPanelOpen,
          })}
        >
          <Button
            className={classNames(styles.navLink, styles.pullRight)}
            variant="tertiary"
            onClick={handleBack}
            data-testid="help-back-btn"
            analyticsName="Back"
          >
            <LeftArrowIcon className={styles.navIcon} />
            <span className={styles.navLabel}>Back</span>
          </Button>
          <LeftHandPane
            showLinkedAccounts={showLinkedAccounts}
            currentSourceType={currentSourceType}
            isLoadingComplete={isComplete}
          />
          {onExitClick && (
            <div className={styles.navExit}>
              <SecondaryCta
                onClick={onExitClick}
                data-testid="help-exit-btn"
                label="Exit"
                analyticsName="Exit"
              />
            </div>
          )}
        </section>
        <section
          className={classNames(styles.pane, styles.rightPane, {
            [styles.noscroll]: isInfoPanelOpen,
          })}
          data-testid="root-right-section"
        >
          {isPlaidClientLimitReached ? (
            <PlaidUpgrade
              integrationLogo={integrationLogo}
              isComplete={isComplete}
            />
          ) : (
            <RightHandPane
              title={title}
              onBackClick={onBackClick}
              onExitClick={onExitClick}
              onInfoClick={handleInfo}
              primaryCtaProps={primaryCtaProps}
              secondaryCtaProps={secondaryCtaProps}
              footer={footer}
              isComplete={isComplete}
            >
              {children}
            </RightHandPane>
          )}
        </section>
      </Card>
      <div
        data-testid="card-loading-overlay"
        className={classNames(styles.overlay, { [styles.show]: !isComplete })}
      >
        <Spinner />
      </div>
    </div>
  );
};
